import { FC, useState, useMemo } from "react";
import { Modal, Checkbox, Button, Input, message, Spin, Empty, Select } from "antd";
import { Bell, Search, Filter, Hammer } from "lucide-react";
import { useGetAllStaff } from "~/services/operation";
import { notificationsAPI } from "~/services/notifications/notifications.api";
import { TStaff } from "~/types";

interface AvailabilityReminderModalProps {
  open: boolean;
  onClose: () => void;
}

// Calculate average rating from staff skill fields
const calculateAverageRating = (staff: TStaff): number => {
  const ratings = [
    staff.installation,
    staff.packing,
    staff.handling,
    staff.clientRelations,
    staff.teamwork,
  ].filter((r) => r !== null && r !== undefined);

  if (ratings.length === 0) return 0;
  return ratings.reduce((sum, r) => sum + r, 0) / ratings.length;
};

export const AvailabilityReminderModal: FC<AvailabilityReminderModalProps> = ({
  open,
  onClose,
}) => {
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sending, setSending] = useState(false);

  // Filter states
  const [filterLead, setFilterLead] = useState<boolean | null>(null);
  const [filterDriver, setFilterDriver] = useState<boolean | null>(null);
  const [filterMinRating, setFilterMinRating] = useState<number | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);

  // Fetch all staff - use default settings
  const { data: staffData, isLoading } = useGetAllStaff({
    page: 1,
    pageSize: 50,
  });

  const staffList = staffData?.items || [];

  // Filter to only show staff with user emails (those who can receive notifications)
  const staffWithEmails = staffList.filter((staff) => staff.user?.email);

  // Get unique status values from staff data
  const availableStatuses = useMemo(() => {
    const statuses = new Set<string>();
    staffWithEmails.forEach((staff) => {
      if (staff.status) statuses.add(staff.status);
    });
    return Array.from(statuses);
  }, [staffWithEmails]);

  // Check if any filter is active
  const hasActiveFilters = filterLead !== null || filterDriver !== null || filterMinRating !== null || filterStatus !== null;

  // Filter staff based on search query and filters
  const filteredStaff = useMemo(() => {
    return staffWithEmails.filter((staff) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const fullName = `${staff.firstName} ${staff.lastName}`.toLowerCase();
        const email = staff.user?.email?.toLowerCase() || "";
        if (!fullName.includes(query) && !email.includes(query)) {
          return false;
        }
      }

      // Lead filter
      if (filterLead !== null && staff.lead !== filterLead) {
        return false;
      }

      // Driver filter
      if (filterDriver !== null && staff.driver !== filterDriver) {
        return false;
      }

      // Rating filter (minimum rating)
      if (filterMinRating !== null) {
        const avgRating = calculateAverageRating(staff);
        if (avgRating < filterMinRating) {
          return false;
        }
      }

      // Status filter
      if (filterStatus !== null && staff.status !== filterStatus) {
        return false;
      }

      return true;
    });
  }, [staffWithEmails, searchQuery, filterLead, filterDriver, filterMinRating, filterStatus]);

  // Check if all filtered staff are selected
  const allSelected = filteredStaff.length > 0 && filteredStaff.every((staff) =>
    selectedStaffIds.includes(staff.id)
  );

  // Check if some (but not all) filtered staff are selected
  const indeterminate = filteredStaff.some((staff) =>
    selectedStaffIds.includes(staff.id)
  ) && !allSelected;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Add all filtered staff to selection
      const allFilteredIds = filteredStaff.map((staff) => staff.id);
      setSelectedStaffIds((prev) => {
        const newSet = new Set([...prev, ...allFilteredIds]);
        return Array.from(newSet);
      });
    } else {
      // Remove all filtered staff from selection
      const filteredIds = new Set(filteredStaff.map((staff) => staff.id));
      setSelectedStaffIds((prev) => prev.filter((id) => !filteredIds.has(id)));
    }
  };

  const handleToggleStaff = (staffId: string) => {
    setSelectedStaffIds((prev) =>
      prev.includes(staffId)
        ? prev.filter((id) => id !== staffId)
        : [...prev, staffId]
    );
  };

  const handleSend = async () => {
    if (selectedStaffIds.length === 0) {
      message.warning("Please select at least one staff member");
      return;
    }

    try {
      setSending(true);
      const result = await notificationsAPI.sendAvailabilityReminder(
        selectedStaffIds
      );

      if (result.success) {
        message.success(result.message);
        onClose();
        setSelectedStaffIds([]);
        setSearchQuery("");
      } else {
        message.error(result.message || "Failed to send availability reminders");
      }
    } catch (error: any) {
      message.error(error?.message || "Failed to send availability reminders");
    } finally {
      setSending(false);
    }
  };

  const handleCancel = () => {
    onClose();
    setSelectedStaffIds([]);
    setSearchQuery("");
    setFilterLead(null);
    setFilterDriver(null);
    setFilterMinRating(null);
    setFilterStatus(null);
  };

  const clearFilters = () => {
    setFilterLead(null);
    setFilterDriver(null);
    setFilterMinRating(null);
    setFilterStatus(null);
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <Bell size={20} />
          <span>Send Availability Reminder</span>
        </div>
      }
      open={open}
      onCancel={handleCancel}
      width={600}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="send"
          type="primary"
          icon={<Bell size={16} />}
          loading={sending}
          onClick={handleSend}
          disabled={selectedStaffIds.length === 0}
        >
          Send to {selectedStaffIds.length} Staff Member
          {selectedStaffIds.length !== 1 ? "s" : ""}
        </Button>,
      ]}
    >
      <div className="space-y-4">
        <p className="text-gray-600">
          Select staff members to send availability reminder emails. They will
          receive an email with a link to update their availability.
        </p>


        {/* Info about staff count */}
        {!isLoading && staffList.length > 0 && (
          <div className="text-sm text-gray-500">
            Showing {staffWithEmails.length} staff member
            {staffWithEmails.length !== 1 ? "s" : ""} with email addresses
            {staffList.length !== staffWithEmails.length && (
              <span className="text-amber-600 ml-1">
                ({staffList.length - staffWithEmails.length} without email)
              </span>
            )}
          </div>
        )}

        {/* Search */}
        <Input
          placeholder="Search by name or email..."
          prefix={<Search size={16} className="text-gray-400" />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
        />

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 p-3 bg-gray-50 rounded-lg border">
          <div className="flex items-center gap-1.5 text-gray-600">
            <Filter size={14} />
            <span className="text-xs font-medium">Filters:</span>
          </div>

          {/* Lead Filter */}
          <Select
            placeholder="Lead"
            value={filterLead}
            onChange={(value) => setFilterLead(value)}
            allowClear
            size="small"
            style={{ width: 90 }}
            options={[
              { label: "Lead", value: true },
              { label: "Not Lead", value: false },
            ]}
          />

          {/* Driver Filter */}
          <Select
            placeholder="Driver"
            value={filterDriver}
            onChange={(value) => setFilterDriver(value)}
            allowClear
            size="small"
            style={{ width: 100 }}
            options={[
              { label: "Driver", value: true },
              { label: "Not Driver", value: false },
            ]}
          />

          {/* Rating Filter */}
          <Select
            placeholder="Rating"
            value={filterMinRating}
            onChange={(value) => setFilterMinRating(value)}
            allowClear
            size="small"
            style={{ width: 90 }}
            options={[
              { label: "1+ ★", value: 1 },
              { label: "2+ ★", value: 2 },
              { label: "3+ ★", value: 3 },
              { label: "4+ ★", value: 4 },
            ]}
          />

          {/* Status Filter */}
          <Select
            placeholder="Status"
            value={filterStatus}
            onChange={(value) => setFilterStatus(value)}
            allowClear
            size="small"
            style={{ width: 130 }}
            options={availableStatuses.map((status) => ({
              label: status.replace(/_/g, " "),
              value: status,
            }))}
          />

          {/* Clear Filters */}
          {hasActiveFilters && (
            <Button
              type="link"
              size="small"
              onClick={clearFilters}
              className="text-xs px-1"
            >
              Clear all
            </Button>
          )}
        </div>

        {/* Select All */}
        {!isLoading && filteredStaff.length > 0 && (
          <div className="border-b pb-3">
            <Checkbox
              checked={allSelected}
              indeterminate={indeterminate}
              onChange={(e) => handleSelectAll(e.target.checked)}
            >
              <span className="font-medium">
                Select All ({filteredStaff.length})
              </span>
            </Checkbox>
          </div>
        )}

        {/* Staff List */}
        <div className="max-h-96 overflow-y-auto border rounded-lg">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <Spin />
            </div>
          ) : filteredStaff.length === 0 ? (
            <Empty
              description={
                searchQuery || hasActiveFilters
                  ? "No staff members found matching your criteria"
                  : "No staff members found"
              }
              className="py-8"
            />
          ) : (
            <div className="divide-y">
              {filteredStaff.map((staff) => {
                const avgRating = calculateAverageRating(staff);
                return (
                  <div
                    key={staff.id}
                    className="p-3 hover:bg-gray-50 transition-colors"
                  >
                    <Checkbox
                      checked={selectedStaffIds.includes(staff.id)}
                      onChange={() => handleToggleStaff(staff.id)}
                    >
                      <div className="ml-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">
                            {staff.firstName} {staff.lastName}
                          </span>
                          {staff.staffRef && (
                            <span className="text-xs text-gray-500 font-mono">
                              ({staff.staffRef})
                            </span>
                          )}
                          <div className="flex gap-1 flex-wrap">
                            {staff.lead && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold">
                                L
                              </span>
                            )}
                            {staff.driver && (
                              <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-semibold">
                                D
                              </span>
                            )}
                            {avgRating > 0 && (
                              <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold flex items-center gap-0.5">
                                <Hammer size={10} />
                                {avgRating.toFixed(1)}/5
                              </span>
                            )}
                            {staff.status && (
                              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                                staff.status === "ACTIVE" ? "bg-emerald-100 text-emerald-700" :
                                staff.status === "AVAILABLE" ? "bg-emerald-100 text-emerald-700" :
                                staff.status === "ON_ASSIGNMENT" ? "bg-purple-100 text-purple-700" :
                                staff.status === "HIATUS" ? "bg-orange-100 text-orange-700" :
                                staff.status === "INACTIVE" ? "bg-gray-100 text-gray-600" :
                                "bg-gray-100 text-gray-600"
                              }`}>
                                {staff.status.replace(/_/g, " ")}
                              </span>
                            )}
                          </div>
                        </div>
                        {staff.user?.email && (
                          <div className="text-sm text-gray-500">
                            {staff.user.email}
                          </div>
                        )}
                      </div>
                    </Checkbox>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
